import { useEffect } from 'react'
import { useMap } from '@wehunt/map'
import type { GeoPt } from '../../lib/types'

/** Projects a geographic point to the map's current screen pixels. */
export type MapProjectFn = (p: GeoPt) => [number, number]

/**
 * The inverse: screen pixels back to a geographic point.
 *
 * Needed by the custom sheet, which derives a page size or a scale FROM what
 * the map is showing — the opposite direction from the fit, which asks where a
 * known sheet lands on screen.
 */
export type MapUnprojectFn = (xy: [number, number]) => GeoPt

export interface MapProjectionProbeProps {
  /**
   * Filled with the live map's projection while mounted, and cleared on
   * unmount. Typed as a `.current` slot rather than a callback prop, so what
   * the write means is the CONSUMER's decision: a plain `useRef` takes the
   * projection silently, while `usePrintFrame` hands in an accessor pair whose
   * setter also bumps a state counter, because its auto-fit has to know the
   * moment a projection appears (corners that arrive first would otherwise
   * find nothing to measure and never be retried). Either way the probe
   * assigns once per map lifetime and never reads back, which is what makes
   * both shapes safe here.
   */
  projectRef: { current: MapProjectFn | null }
  /**
   * The same slot for the inverse projection, for a surface that reads a page
   * size or a scale out of the viewport. Optional and a PLAIN ref by design:
   * unlike `projectRef` it is read on demand — when the camera stops — rather
   * than by an effect that has to wake when it appears, so it needs no
   * render-triggering accessor.
   */
  unprojectRef?: { current: MapUnprojectFn | null }
}

/**
 * Hands the live map's screen projection out to the surface hosting it.
 *
 * `useMap()` only resolves inside `MapView`'s children, so anything outside it
 * — a print panel's Fit action, say — cannot ask the map where a coordinate
 * lands on screen. This renders nothing and exists purely to bridge that: one
 * child inside the map, writing the projection into a ref the host owns. The
 * alternative, reaching for the MapLibre instance from the host, would mean
 * either lifting the map handle out of `MapView` or keeping a second reference
 * to it, both of which put the map's lifetime in two places.
 */
export function MapProjectionProbe({ projectRef, unprojectRef }: MapProjectionProbeProps): null {
  const map = useMap()
  useEffect(() => {
    if (!map) return
    projectRef.current = (p: GeoPt) => {
      const { x, y } = map.project([p.lon, p.lat])
      return [x, y]
    }
    if (unprojectRef) {
      unprojectRef.current = ([x, y]: [number, number]) => {
        const { lat, lng } = map.unproject([x, y])
        return { lat, lon: lng }
      }
    }
    return () => {
      projectRef.current = null
      if (unprojectRef) unprojectRef.current = null
    }
  }, [map, projectRef, unprojectRef])
  return null
}
